import type {
  OrganizationAddress,
  OrganizationPresentation,
} from "../../../../../api/public/presentation"
export { formatServiceHoursLines } from "../../../../_shared/_logic/service-hours-presentation"
import type { OrgPresentationMode } from "../../../_shared/_logic/presentation-mode"

export type BusinessFaqItem = {
  question: string
  answer: string
}

const normalizeText = (value?: string | null): string | null => {
  const text = value?.trim()

  return text || null
}

export const formatPhoneHref = (phone?: string | null): string | null => {
  const text = normalizeText(phone)

  if (!text) return null

  const dialable = text.replace(/[^\d+]/g, "")

  return dialable ? `tel:${dialable}` : null
}

export const formatMailHref = (email?: string | null): string | null => {
  const text = normalizeText(email)

  return text ? `mailto:${text}` : null
}

export const formatAddressLines = (address?: OrganizationAddress | null): string[] => {
  if (!address) return []

  const streetLine = [address.street, address.house_number]
    .map(normalizeText)
    .filter(Boolean)
    .join(" ")
  const cityLine = [address.zip, address.city].map(normalizeText).filter(Boolean).join(" ")
  const country = normalizeText(address.country)

  return [
    streetLine,
    normalizeText(address.address_extra),
    cityLine,
    country && !["de", "deutschland", "germany"].includes(country.toLowerCase()) ? country : null,
  ].filter((line): line is string => Boolean(line))
}

export const buildBusinessFaqItems = ({
  organization,
  presentationMode,
  hasCode,
}: {
  organization: OrganizationPresentation | null
  presentationMode: OrgPresentationMode
  hasCode: boolean
}): BusinessFaqItem[] => {
  const orgName = normalizeText(organization?.name)
  const orgNominative = orgName ?? "Ihr Schornsteinfegerbetrieb"
  const orgDative = orgName ?? "Ihrem Schornsteinfegerbetrieb"
  const orgAccusative = orgName ?? "Ihren Schornsteinfegerbetrieb"
  const hasTerminbot = presentationMode !== "appointment_portal_only"

  const baseItems: BusinessFaqItem[] = [
    {
      question: "Was ist KehrBlatt?",
      answer: `KehrBlatt stellt diese Terminseite im Auftrag von ${orgDative} bereit. Sie können hier Schornsteinfegertermine online abstimmen oder ein Anliegen an den Betrieb übermitteln.`,
    },
    {
      question: "Muss ich danach noch anrufen?",
      answer: `In der Regel nicht. Ihre Auswahl wird an ${orgAccusative} übermittelt. Falls noch etwas unklar ist, meldet sich der Betrieb über die angegebenen Kontaktdaten bei Ihnen.`,
    },
    {
      question: "Warum werden Kontaktdaten abgefragt?",
      answer: `Damit ${orgNominative} Sie erreichen kann, falls zu Ihrem Termin noch etwas geklärt werden muss. Wenn Sie Kontaktdaten angeben, werden sie im Auftrag des Betriebs verarbeitet und können zukünftige Terminabstimmungen erleichtern.`,
    },
  ]

  if (hasCode) {
    return [
      ...baseItems,
      {
        question: "Was kann ich mit meinem Terminlink tun?",
        answer:
          "Der Link wurde erkannt. Sie können den vorgeschlagenen Termin bestätigen, einen anderen Termin wählen, absagen oder mitteilen, dass Sie nicht zuständig sind.",
      },
      {
        question: "Mein Termin passt nicht. Was soll ich auswählen?",
        answer:
          "Wählen Sie auf der Terminseite die Option zum Verschieben oder Ändern. Danach können Sie einen passenden Vorschlag auswählen oder Ihren Wunsch mitteilen.",
      },
    ]
  }

  if (hasTerminbot) {
    return [
      ...baseItems,
      {
        question: "Was kann ich ohne Code tun?",
        answer: `Sie können KehrAssist starten und Ihr Anliegen schildern. Die Assistenz fragt bei Bedarf Name, Adresse und Wohnung ab, damit ${orgNominative} Ihre Anfrage einordnen kann.`,
      },
      {
        question: "Wobei hilft KehrAssist?",
        answer:
          "KehrAssist hilft bei Terminwünschen, Verschiebungen, Absagen, Rückfragen und einfachen Anliegen rund um den Schornsteinfegertermin.",
      },
      {
        question: "Ich habe doch einen Code. Wo gebe ich ihn ein?",
        answer:
          "Wenn Sie einen Code aus Brief, E-Mail, mobiler Nachricht oder QR-Code haben, geben Sie ihn auf der Terminseite ein. Dann kann Ihre Rückmeldung direkt zum passenden Termin geöffnet werden.",
      },
    ]
  }

  return [
    ...baseItems,
    {
      question: "Wo bekomme ich meinen Code her?",
      answer: `Wenn ${orgNominative} Ihnen einen Termin angekündigt hat, steht der Code üblicherweise im Brief, in der E-Mail, in einer mobilen Nachricht oder hinter dem persönlichen QR-Code. Falls er fehlt, kontaktieren Sie den Betrieb über die Kontaktdaten auf dieser Seite.`,
    },
    {
      question: "Kann ich ohne Code einen Termin online ändern?",
      answer: `Ohne Code kann diese Terminseite Ihren Termin nicht sicher finden. Bitte wenden Sie sich direkt an ${orgAccusative} per Telefon oder E-Mail.`,
    },
  ]
}
